import React from 'react';

const About = () => {
  const creativeAcronym = [
    { letter: 'C', meaning: 'Citadel of Success' },
    { letter: 'R', meaning: 'Raising with a Difference' },
    { letter: 'E', meaning: 'Education at its Peak' },
    { letter: 'A', meaning: 'Adequate' },
    { letter: 'T', meaning: 'Trusted Care' },
    { letter: 'I', meaning: 'Ideal' },
    { letter: 'V', meaning: 'Virtue of Excellence' },
    { letter: 'E', meaning: 'Efficiency Guaranteed' }
  ];

  const features = [
    {
      icon: 'Other',
      title: 'Smart App Features',
      description: 'Our comprehensive mobile app provides parents and students with real-time access to attendance records, assignments, grades, and school announcements. Interactive learning tools and instant communication make education seamless.',
      highlights: ['Real-time Updates', 'Assignment Tracking', 'Grade Monitoring', 'Instant Communication']
    },
    {
      icon: '📹',
      title: 'CCTV Monitoring',
      description: 'Parents can monitor their children\'s classroom activities through our secure 24/7 CCTV system. This advanced monitoring ensures safety, allows parents to stay connected, and provides peace of mind.',
      highlights: ['24/7 Access', 'Secure Monitoring', 'Parent Peace of Mind', 'Safety Assurance']
    },
    {
      icon: '🌍',
      title: 'Language Learning Excellence',
      description: 'Our bilingual education program immerses students in both English and local languages, fostering cultural understanding and global communication skills essential for the modern world.',
      highlights: ['Bilingual Curriculum', 'Cultural Immersion', 'Global Perspectives', 'Language Proficiency']
    },
    {
      icon: '👨‍School',
      title: 'Degree Holder Teachers',
      description: 'Our faculty consists of highly qualified degree holders with specialized training in early childhood education. Each teacher brings expertise, passion, and dedication to nurturing young minds.',
      highlights: ['Qualified Educators', 'Specialized Training', 'Passionate Teaching', 'Professional Development']
    },
    {
      icon: '⚡',
      title: 'High-Tech Infrastructure',
      description: 'Powered by a reliable 24/7 solar energy system, our classrooms feature modern technology including smart boards, computers, and interactive learning tools that enhance the educational experience.',
      highlights: ['Solar Power System', 'Smart Classrooms', 'Modern Technology', 'Reliable Infrastructure']
    }
  ];

  const skills = [
    {
      icon: '🍞',
      title: 'Bakery',
      description: 'Master the art of baking with hands-on training in cake decorating, pastry making, and culinary techniques that prepare students for entrepreneurial success.',
      highlights: ['Cake Decorating', 'Pastry Making', 'Culinary Techniques', 'Entrepreneurial Skills']
    },
    {
      icon: '🧵',
      title: 'Tailoring',
      description: 'Learn professional sewing skills, fashion design, and garment construction to build a foundation for careers in fashion and textile industries.',
      highlights: ['Sewing Techniques', 'Fashion Design', 'Garment Construction', 'Textile Knowledge']
    },
    {
      icon: '🎵',
      title: 'Music',
      description: 'Develop musical talents through comprehensive training in various instruments, voice training, and music theory to foster artistic expression.',
      highlights: ['Instrument Training', 'Voice Development', 'Music Theory', 'Performance Skills']
    },
    {
      icon: '💻',
      title: 'Computer Skills',
      description: 'Gain essential computer literacy including programming, digital design, and office applications to thrive in the digital age.',
      highlights: ['Programming Basics', 'Digital Design', 'Office Applications', 'IT Fundamentals']
    },
    {
      icon: '🎨',
      title: 'Arts & Crafts',
      description: 'Explore creative expression through painting, drawing, and various craft techniques that enhance fine motor skills and imagination.',
      highlights: ['Painting & Drawing', 'Craft Techniques', 'Fine Motor Skills', 'Creative Expression']
    },
    {
      icon: '🏗️',
      title: 'Carpentry',
      description: 'Learn woodworking fundamentals, tool usage, and construction techniques for hands-on building and repair skills.',
      highlights: ['Woodworking', 'Tool Usage', 'Construction Basics', 'Repair Skills']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-primary to-blue-700 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            About Creative Town School
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            Where innovation meets education, and every child discovers their limitless potential
          </p>
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-8 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-yellow-300">What CREATIVE Stands For</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {creativeAcronym.map((item, index) => (
                <div key={index} className="bg-white bg-opacity-20 rounded-lg p-4 hover:bg-opacity-30 transition duration-300">
                  <div className="text-2xl font-bold mb-2 text-yellow-300">{item.letter}</div>
                  <div className="text-sm font-medium">{item.meaning}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Choose Creative Town?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine cutting-edge technology with traditional values to create an unparalleled learning experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {features.slice(0, 3).map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition duration-300 overflow-hidden group">
                <div className="p-8">
                  <div className="text-5xl mb-6 text-center">{feature.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{feature.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {feature.highlights.map((highlight, idx) => (
                      <div key={idx} className="bg-blue-50 text-blue-700 px-3 py-2 rounded-full text-sm font-medium text-center">
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.slice(3).map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition duration-300 overflow-hidden group">
                <div className="p-8">
                  <div className="text-5xl mb-6 text-center">{feature.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{feature.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {feature.highlights.map((highlight, idx) => (
                      <div key={idx} className="bg-blue-50 text-blue-700 px-3 py-2 rounded-full text-sm font-medium text-center">
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skill Training Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Skill Training Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive vocational training equips students with practical skills for real-world success and entrepreneurial opportunities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {skills.map((skill, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition duration-300 overflow-hidden group">
                <div className="p-8">
                  <div className="text-5xl mb-6 text-center">{skill.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{skill.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{skill.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {skill.highlights.map((highlight, idx) => (
                      <div key={idx} className="bg-green-50 text-green-700 px-3 py-2 rounded-full text-sm font-medium text-center">
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-6 bg-primary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">Our Mission</h2>
          <p className="text-xl md:text-2xl leading-relaxed mb-8">
            To nurture young minds through innovative education, combining traditional values with modern technology,
            ensuring every child receives the care, knowledge, and skills needed to excel in an ever-changing world.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-2xl font-bold mb-3 text-yellow-300">Excellence</h3>
              <p>Striving for the highest standards in education and character development</p>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-2xl font-bold mb-3 text-yellow-300">Innovation</h3>
              <p>Embracing technology and modern teaching methods for better learning outcomes</p>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-2xl font-bold mb-3 text-yellow-300">Care</h3>
              <p>Providing a nurturing environment where every child feels valued and supported</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Join Our Creative Community?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Experience the difference that Creative Town Bilingual School can make in your child's education
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/enrollment"
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition duration-300 transform hover:scale-105 shadow-lg"
            >
              Enroll Now
            </a>
            <a
              href="/contact"
              className="border-2 border-primary text-primary px-8 py-3 rounded-full font-semibold hover:bg-primary hover:text-white transition duration-300 transform hover:scale-105"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;



